import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import LoadingAnimation from "../../../common/LoadingAnimation";
import CustomProgressCircularLoader from "../../../common/CustomProgressCircularLoader";
import { programLevels, genders } from "../../../constants";
import useSVG from "../../../hooks/useSVG";
import useProgramProps from "../../../hooks/useProgramProps";
import usePrograms from "../../../hooks/usePrograms";

import { ResetProgramActivity, DeleteSelectedProgram, GenerateScheduleOldUser, LoadAccountDetails } from "../../../redux/v2/user/actions";
import CustomButton from "../../../common/CustomButton";
import { delay, removeItemInArray } from "../../../utils";
import { socketS2 } from "../../../utils/socket";

function ResetProgram(props) {
    const navigate = useNavigate();

    const { account } = props.User;
    const { programs } = props.Program;
    const { setShowModal, selected, perc, programIsFull } = props;

    const [activeButton, setActiveButton] = useState("");
    const [isOngoingAct, setIsOngoingAct] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    const [selectedProg, setSelected] = useState(null);
    const [ongoingUpdateInWP, setOngoingUpdateInWP] = useState(false);
    const [wpProgress, setWPProgress] = useState({ progress: 0, message: "Working on it" });

    const { UseProgramSVG } = useSVG();
    const { GetSelectedPrograms } = usePrograms({ programs: programs });
    const { key } = useProgramProps(selectedProg);

    const gradient = selectedProg?.gradient?.split("-")
    const svgKey = `${programLevels[selectedProg?.level]?.toLowerCase()}${selectedProg?.svg}`;
    const svg = UseProgramSVG(svgKey);
    const programName = `${selectedProg?.title.toLowerCase()} ${programLevels[selectedProg?.level]}`;

    const onboardingDetail = account?.onboardingDetails?.[account?.onboardingDetails?.length - 1];
    const programsInWP = onboardingDetail?.programs?.split(",");
    const detailedProgramsInWP = GetSelectedPrograms(programsInWP || []);
    const isInWP = programsInWP?.includes(key);

    const ListenWPProgress = (id) => {
        socketS2.removeAllListeners(`update-workout-plan-generate-progress-${id}`);
        socketS2.on(`update-workout-plan-generate-progress-${id}`, (data) => {
            setWPProgress(data?.data);
        });
    };

    const GetPayload = ({ add = true }) => {
        return {
            "personalInfo":
            {
                "dob": account?.user?.dob,
                "firstName": account?.user?.first_name,
                "gender": genders[account?.user?.gender],
                "lastName": account?.user?.last_name
            },
            "daysToWorkout": JSON.parse(onboardingDetail?.preferred_days),
            "minutesPerWeek": onboardingDetail?.preferred_min_per_week,
            "consistency": onboardingDetail?.consistency,
            "preferedWorkoutLength": onboardingDetail?.preferred_workout_length,
            "goals": [],
            "programs": add ? [...detailedProgramsInWP, { ...selectedProg }] : [...(removeItemInArray(detailedProgramsInWP, { key: key }, ["key"]))]
        }
    };

    const RemoveProgram = async () => {
        try {
            if (!isOngoingAct && !ongoingUpdateInWP) {
                ListenWPProgress(account?.user?.id);
                setIsOngoingAct(true);
                const res = await props.DeleteSelectedProgram(selectedProg);
                if (res) {
                    setIsOngoingAct(false);
                    if (isInWP) {
                        setOngoingUpdateInWP(true);
                        const payload = GetPayload({ add: false });
                        const res2 = await props.GenerateScheduleOldUser(payload);

                        props.LoadAccountDetails(false, 1);
                        if (res2) {
                            delay(() => {
                                setIsFinished(true);
                                setOngoingUpdateInWP(false);
                                delay(() => {
                                    setIsFinished(false);
                                    setShowModal(false);
                                    setWPProgress({ progress: 0, message: "Working on it" })
                                }, 5000);
                            }, 2000);
                        }
                    } else {
                        props.LoadAccountDetails(false, 1);
                        setIsFinished(true);
                    }
                } else {
                    setActiveButton("Remove");
                    setIsOngoingAct(false);
                    toast.error("Failed to Reset!");
                }
            }
        } catch (e) {
        }
    };

    const AddToSchedule = async () => {

        if (!ongoingUpdateInWP) {
            setActiveButton("Schedule");

            ListenWPProgress(account?.user?.id);
            setOngoingUpdateInWP(true);

            const payload = GetPayload({ add: true });
            const res = await props.GenerateScheduleOldUser(payload);

            if (res) {
                props?.LoadAccountDetails(false, 1);

                delay(() => {
                    setIsFinished(true);
                    setOngoingUpdateInWP(false);

                    delay(() => {
                        setIsFinished(false);
                        setShowModal(false);
                        setWPProgress({ progress: 0, message: "Working on it" })
                    }, 5000);
                }, 2000);


            }
        }
    }

    useEffect(() => {
        setSelected(selected);
        setActiveButton("");
        setIsOngoingAct(false);
        setIsFinished(false);
    }, [selected?.id, selected?.level]);

    return (
        <>
            <div id="reset-program">

                <div className="rp-card-icon">
                    <div style={{ backgroundImage: `linear-gradient(to right, ${gradient?.[0]}, ${gradient?.[1]})` }}>
                        {svg}
                    </div>
                </div>

                {
                    !isFinished &&
                    <div id="rp-header">
                        <h4>{`${selectedProg?.title.toUpperCase()} ${programLevels[selectedProg?.level]}`}</h4>
                        {!ongoingUpdateInWP && <h5>{perc}% Complete</h5>}
                    </div>
                }

                <div id="rp-content">
                    {
                        !activeButton
                            ? <div id="rp-buttons">
                                <div id="rp-buttons-wrapper">
                                    <CustomButton onClick={() => {
                                        const { id, level } = selectedProg;
                                        let slug = "program=" + id + ((level && level != 0) ? ("&level=" + level) : "")
                                        setShowModal(false);
                                        navigate("/program-detail?" + slug)

                                    }}>
                                        Go to Program
                                    </CustomButton>
                                    {
                                        (!programIsFull && !selected?.isInWP && !programIsFull) && <CustomButton onClick={AddToSchedule}>
                                            Add to Schedule
                                        </CustomButton>
                                    }
                                    <CustomButton onClick={() => setActiveButton("Restart")}>
                                        Restart
                                    </CustomButton>
                                    <CustomButton isPrimary={false} onClick={() => setActiveButton("Remove")}>
                                        Remove
                                    </CustomButton>
                                </div>
                            </div>
                            : <div id="rp-description">
                                {
                                    (activeButton == "Restart" && !isFinished) &&
                                    <>
                                        {
                                            isOngoingAct
                                                ? <LoadingAnimation />
                                                : <h5>
                                                    Restarting this program
                                                    will reset your progress
                                                    bar to 0%. You will not
                                                    lose credit for
                                                    completed workouts.
                                                </h5>
                                        }

                                        <CustomButton onClick={async () => {
                                            setIsOngoingAct(true);
                                            const res = await props.ResetProgramActivity(selectedProg);
                                            if (res) {
                                                setIsFinished(true);
                                                setIsOngoingAct(false);
                                            } else {
                                                setActiveButton("Restart");
                                                setIsOngoingAct(false);
                                                toast.error("Failed to Reset!");
                                            }
                                        }}>
                                            Restart
                                        </CustomButton>
                                    </>
                                }

                                {
                                    (activeButton == "Remove" && !isFinished) &&
                                    <>
                                        {
                                            !ongoingUpdateInWP
                                                ? <>
                                                    {
                                                        isOngoingAct
                                                            ? <LoadingAnimation />
                                                            : <>
                                                                <h5>
                                                                    Removing this program
                                                                    will reset your progress
                                                                    bar to 0%. You will not
                                                                    lose credit for
                                                                    completed workouts.
                                                                </h5>

                                                                {isInWP && <h5>
                                                                    This program is part of your workout plan.
                                                                    Removing it will affect your workout plan.
                                                                </h5>}
                                                            </>
                                                    }
                                                </>
                                                : <div id="rp-d-cpcl-wrapper" >
                                                    <CustomProgressCircularLoader wpProgress={wpProgress} />
                                                    <em>Updating workout plan...</em>
                                                </div>
                                        }
                                        {(!isOngoingAct && !ongoingUpdateInWP) && <CustomButton onClick={RemoveProgram}>
                                            Remove
                                        </CustomButton>}
                                    </>
                                }

                                {
                                    (activeButton == "Schedule" && !isFinished) && <>

                                        <div id="rp-d-cpcl-wrapper" >
                                            <CustomProgressCircularLoader wpProgress={wpProgress} />
                                            <em>Updating workout plan...</em>
                                        </div>
                                    </>
                                }

                                {
                                    isFinished &&
                                    <>
                                        <div>
                                            {
                                                !isInWP
                                                    ? <h4>Success</h4>
                                                    : <h4 className="t-capitalize">{programName} {activeButton == "Schedule" ? "Added" : "Removed"}</h4>
                                            }
                                            <h5>
                                                {
                                                    activeButton == "Restart"
                                                        ? "The program has been restarted."
                                                        : (isInWP ? "Your workout plan has been updated." : "The program has been removed.")
                                                }
                                            </h5>
                                        </div>
                                        <CustomButton onClick={() => {
                                            setIsFinished(false);
                                            setShowModal(false);
                                            setWPProgress({ progress: 0, message: "Working on it" })
                                        }}>
                                            Finished
                                        </CustomButton>
                                    </>
                                }
                            </div>
                    }

                </div>
            </div >
        </>
    );
}

const mapStateToProps = (state) => ({
    User: state.User,
    Program: state.Program,
});

export default connect(mapStateToProps, {
    ResetProgramActivity,
    DeleteSelectedProgram,
    GenerateScheduleOldUser,
    LoadAccountDetails,
})(ResetProgram);